import { SubjectManagementStatus } from 'src/app/services/models/subjects/subject.model';

export type SubjectManagementDetailStatus =
  | 'draft'
  | 'in_progress'
  | 'special_sent_back'
  | 'sent_back'
  | 'pending'
  | 'pending_approval'
  | 'approved';

export interface SubjectManagementStatusDisplay<TStatus extends string = string> {
  status: TStatus;
  label: string;
}

const COMPLETED_STATUS_CODES = new Set([
  'approved',
  'completed',
  'education_head_confirmed',
  'vice_dean_confirmed',
  'locked',
]);

const SECTION_STATUS_PRIORITY: Record<string, number> = {
  special_revision_requested: 1,
  revision_requested: 2,
  ready_for_teacher: 3,
  teacher_confirmed: 4,
  imported_pending_fix: 5,
  draft: 5,
  education_head_confirmed: 6,
  vice_dean_confirmed: 6,
  locked: 6,
};

export function normalizeSubjectManagementStatus(status: string | null | undefined): string {
  return String(status ?? '').trim();
}

export function normalizeTeachingApprovalStatus(status: string | null | undefined): string {
  const normalizedStatus = normalizeSubjectManagementStatus(status);

  if (normalizedStatus === 'approved' || normalizedStatus === 'completed') {
    return 'education_head_confirmed';
  }

  if (
    normalizedStatus === 'submitted' ||
    normalizedStatus === 'pending' ||
    normalizedStatus === 'pending_approval' ||
    normalizedStatus === 'in_progress'
  ) {
    return 'ready_for_teacher';
  }

  if (normalizedStatus === 'rejected' || normalizedStatus === 'sent_back') {
    return 'revision_requested';
  }

  return normalizedStatus;
}

export function mapTeachingApprovalStatus(
  status: string | null | undefined,
): SubjectManagementStatusDisplay<SubjectManagementDetailStatus> {
  switch (normalizeTeachingApprovalStatus(status)) {
    case 'locked':
    case 'vice_dean_confirmed':
    case 'education_head_confirmed':
      return { status: 'approved', label: 'รับรองแล้ว' };
    case 'teacher_confirmed':
      return { status: 'pending_approval', label: 'รอพิจารณารับรอง' };
    case 'ready_for_teacher':
      return { status: 'pending', label: 'รอดำเนินการ' };
    case 'imported_pending_fix':
    case 'draft':
      return { status: 'draft', label: 'แบบร่าง' };
    case 'special_revision_requested':
      return { status: 'special_sent_back', label: 'ส่งกลับแก้ไขพิเศษ' };
    case 'rejected':
    case 'revision_requested':
      return { status: 'sent_back', label: 'ส่งกลับแก้ไข' };
    default:
      return { status: 'pending', label: 'รอดำเนินการ' };
  }
}

export function mapSubjectManagementOverallStatus(
  status: string | null | undefined,
): SubjectManagementStatusDisplay<SubjectManagementStatus> {
  const teachingStatus = mapTeachingApprovalStatus(status);

  return {
    status: teachingStatus.status === 'approved' ? 'completed' : teachingStatus.status,
    label: teachingStatus.label,
  };
}

export function mapSubjectManagementDetailMainStatus(
  status: string | null | undefined,
): SubjectManagementStatusDisplay<SubjectManagementDetailStatus> {
  return mapTeachingApprovalStatus(status);
}

function sectionStatusPriority(status: string): number {
  return SECTION_STATUS_PRIORITY[status] ?? SECTION_STATUS_PRIORITY.ready_for_teacher;
}

export function mapSectionAggregatedStatus(
  statuses: (string | null | undefined)[],
  _mode: 'shared' | 'independent',
  fallbackStatus?: string | null,
): SubjectManagementStatusDisplay<SubjectManagementDetailStatus> {
  const normalizedStatuses = statuses
    .map((status) => normalizeTeachingApprovalStatus(status))
    .filter((status) => status !== '');

  if (normalizedStatuses.length === 0) {
    const normalizedFallback = normalizeTeachingApprovalStatus(fallbackStatus);
    return normalizedFallback === 'draft' || normalizedFallback === 'imported_pending_fix'
      ? mapTeachingApprovalStatus('draft')
      : mapTeachingApprovalStatus('ready_for_teacher');
  }

  const draftStatuses = new Set(['draft', 'imported_pending_fix']);
  if (normalizedStatuses.every((status) => draftStatuses.has(status))) {
    return mapTeachingApprovalStatus('imported_pending_fix');
  }

  return mapTeachingApprovalStatus(
    normalizedStatuses.reduce((lowestStatus, status) => (
      sectionStatusPriority(status) < sectionStatusPriority(lowestStatus)
        ? status
        : lowestStatus
    )),
  );
}

export function isSubjectManagementCompletedStatus(status: string | null | undefined): boolean {
  return COMPLETED_STATUS_CODES.has(normalizeSubjectManagementStatus(status));
}
